import { formatWhatsappMask, whatsappDigits } from "@/lib/cardapio/formFields";

export const SEGMENTO_EMPRESA_OPCOES = [
  "Imobiliária",
  "Escritório",
  "Clínica",
  "Loja",
  "Construtora",
  "Agência",
  "Contabilidade",
  "Outros",
] as const;

export type EmpresaParceiraForm = {
  nome_empresa: string;
  cnpj: string;
  celular_empresa: string;
  segmento: string;
  endereco: string;
  quantidade_colaboradores: string;
  responsavel_nome_cargo: string;
  responsavel_whatsapp: string;
  colaboradores_nomes: string[];
};

export type EmpresaParceira = {
  id: number;
  nome_empresa: string;
  cnpj: string;
  celular_empresa: string;
  segmento: string;
  endereco: string;
  quantidade_colaboradores: number;
  responsavel_nome_cargo: string;
  responsavel_whatsapp: string;
  colaboradores_beneficiados: string[] | null;
  created_at: string;
  updated_at: string | null;
};

export const EMPTY_EMPRESA_PARCEIRA_FORM: EmpresaParceiraForm = {
  nome_empresa: "",
  cnpj: "",
  celular_empresa: "",
  segmento: "",
  endereco: "",
  quantidade_colaboradores: "",
  responsavel_nome_cargo: "",
  responsavel_whatsapp: "",
  colaboradores_nomes: [],
};

export function formatCelularMask(value: string): string {
  return formatWhatsappMask(value);
}

export function cnpjDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, 14);
}

export function formatCnpjDisplay(value: string): string {
  const d = cnpjDigits(value);
  if (d.length !== 14) return value.trim();
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

export function formatPhoneDisplay(value: string): string {
  const d = whatsappDigits(value);
  if (d.length < 10) return value.trim();
  if (d.length === 10) {
    return d.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
  }
  return d.replace(/^(\d{2})(\d)(\d{4})(\d{4})$/, "($1) $2 $3-$4");
}

export function parseColaboradoresBeneficiados(raw: string[] | string | null | undefined): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((n) => String(n));
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "Array") return [];
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (Array.isArray(parsed)) return parsed.map((n) => String(n));
  } catch {
    return trimmed
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }
  return [];
}

export function syncColaboradoresNomes(quantidade: number, current: string[]): string[] {
  if (quantidade <= 0) return [];
  const next = current.slice(0, quantidade);
  while (next.length < quantidade) next.push("");
  return next;
}

export function empresaParceiraToForm(empresa: EmpresaParceira): EmpresaParceiraForm {
  const nomes = parseColaboradoresBeneficiados(empresa.colaboradores_beneficiados);
  const qtd = empresa.quantidade_colaboradores > 0 ? empresa.quantidade_colaboradores : nomes.length;
  return {
    nome_empresa: empresa.nome_empresa,
    cnpj: formatCnpjDisplay(empresa.cnpj),
    celular_empresa: formatCelularMask(empresa.celular_empresa),
    segmento: empresa.segmento,
    endereco: empresa.endereco,
    quantidade_colaboradores: qtd > 0 ? String(qtd) : "",
    responsavel_nome_cargo: empresa.responsavel_nome_cargo,
    responsavel_whatsapp: formatCelularMask(empresa.responsavel_whatsapp),
    colaboradores_nomes: syncColaboradoresNomes(qtd, nomes),
  };
}

export function validateEmpresaParceiraStep1(form: EmpresaParceiraForm): string | null {
  if (!form.nome_empresa.trim()) return "Nome da empresa é obrigatório";
  if (cnpjDigits(form.cnpj).length !== 14) return "CNPJ inválido";
  if (whatsappDigits(form.celular_empresa).length < 10) return "Número de celular da empresa inválido";
  if (!form.segmento.trim()) return "Segmento da empresa é obrigatório";
  if (!form.endereco.trim()) return "Endereço da empresa é obrigatório";
  return null;
}

export function validateEmpresaParceiraStep2(form: EmpresaParceiraForm): string | null {
  if (!form.responsavel_nome_cargo.trim()) return "Nome do responsável é obrigatório";
  if (whatsappDigits(form.responsavel_whatsapp).length < 10) return "WhatsApp do responsável inválido";
  return null;
}

export function validateEmpresaParceiraStep3(
  form: EmpresaParceiraForm,
  options?: { quantidadeOpcional?: boolean },
): string | null {
  const qtdStr = form.quantidade_colaboradores.trim();
  if (!qtdStr || !/^\d+$/.test(qtdStr)) {
    return options?.quantidadeOpcional ? null : "Quantidade de colaboradores é obrigatória";
  }
  const qtd = parseInt(qtdStr, 10);
  if (qtd <= 0) {
    return options?.quantidadeOpcional ? null : "Informe uma quantidade válida de colaboradores";
  }
  if (form.colaboradores_nomes.length !== qtd) return "Preencha todos os nomes dos colaboradores";
  for (let i = 0; i < qtd; i++) {
    if (!form.colaboradores_nomes[i]?.trim()) {
      return `Nome do colaborador ${i + 1} é obrigatório`;
    }
  }
  return null;
}

export function validateEmpresaParceiraForm(
  form: EmpresaParceiraForm,
  options?: { quantidadeOpcional?: boolean },
): string | null {
  const s1 = validateEmpresaParceiraStep1(form);
  if (s1) return s1;
  const s2 = validateEmpresaParceiraStep2(form);
  if (s2) return s2;
  const s3 = validateEmpresaParceiraStep3(form, options);
  if (s3) return s3;
  return null;
}

export function empresaParceiraFormToPayload(form: EmpresaParceiraForm) {
  const qtdStr = form.quantidade_colaboradores.trim();
  const qtd = qtdStr === "" ? 0 : parseInt(qtdStr, 10);
  const nomes = qtd > 0 ? form.colaboradores_nomes.map((n) => n.trim()) : [];
  return {
    nome_empresa: form.nome_empresa.trim(),
    cnpj: cnpjDigits(form.cnpj),
    celular_empresa: whatsappDigits(form.celular_empresa),
    segmento: form.segmento.trim(),
    endereco: form.endereco.trim(),
    quantidade_colaboradores: Number.isFinite(qtd) && qtd > 0 ? qtd : 0,
    responsavel_nome_cargo: form.responsavel_nome_cargo.trim(),
    responsavel_whatsapp: whatsappDigits(form.responsavel_whatsapp),
    colaboradores_beneficiados: nomes,
  };
}

export function empresaParceiraFormsEqual(a: EmpresaParceiraForm, b: EmpresaParceiraForm): boolean {
  return (
    a.nome_empresa === b.nome_empresa &&
    a.cnpj === b.cnpj &&
    a.celular_empresa === b.celular_empresa &&
    a.segmento === b.segmento &&
    a.endereco === b.endereco &&
    a.quantidade_colaboradores === b.quantidade_colaboradores &&
    a.responsavel_nome_cargo === b.responsavel_nome_cargo &&
    a.responsavel_whatsapp === b.responsavel_whatsapp &&
    JSON.stringify(a.colaboradores_nomes) === JSON.stringify(b.colaboradores_nomes)
  );
}

export function clubeEmpresasDisplayName(config: {
  nome_empresa?: string;
  fantasy_name?: string;
  name?: string;
}): string {
  const n =
    config.nome_empresa?.trim() ||
    config.fantasy_name?.trim() ||
    config.name?.trim() ||
    "";
  return n;
}

export function handleQuantidadeColaboradoresChange(
  form: EmpresaParceiraForm,
  rawValue: string,
  maxDigits?: number,
): Partial<EmpresaParceiraForm> {
  let digits = rawValue.replace(/\D/g, "");
  if (maxDigits != null) digits = digits.slice(0, maxDigits);
  const qtd = digits === "" ? 0 : parseInt(digits, 10);
  return {
    quantidade_colaboradores: digits,
    colaboradores_nomes: syncColaboradoresNomes(qtd, form.colaboradores_nomes),
  };
}

/** Apenas letras (incl. acentos), espaços e cedilha — para nomes de colaboradores no cadastro público. */
export function sanitizeColaboradorNomeInput(value: string): string {
  return value.replace(/[^\p{L}\s]/gu, "");
}

export function segmentoSelectOptions(currentValue: string): string[] {
  const base = [...SEGMENTO_EMPRESA_OPCOES];
  const trimmed = currentValue.trim();
  if (trimmed && !base.includes(trimmed as (typeof SEGMENTO_EMPRESA_OPCOES)[number])) {
    return [trimmed, ...base];
  }
  return base;
}
